import {
    BLEND_NORMAL,
    CULLFACE_NONE,
    Entity,
    Mesh,
    MeshInstance,
    PRIMITIVE_LINES,
    StandardMaterial,
    Vec3,
    type GraphicsDevice
} from 'playcanvas';

import type { Scene } from '../scene';

const screenVec = new Vec3();

function formatDegDms(deg: number, kind: 'lat' | 'lng'): string {
    if (!Number.isFinite(deg)) return '—';
    const abs = Math.abs(deg);
    const d = Math.floor(abs);
    const mFloat = (abs - d) * 60;
    const m = Math.floor(mFloat);
    const s = (mFloat - m) * 60;
    const hemi = kind === 'lat' ? (deg >= 0 ? 'N' : 'S') : deg >= 0 ? 'E' : 'W';
    return `${d}°${String(m).padStart(2, '0')}'${s.toFixed(2).padStart(5, '0')}"${hemi}`;
}

/** Unit cross in local space — scaled on entity to match scene size. */
function createUnitCrossMesh(device: GraphicsDevice): Mesh {
    const mesh = new Mesh(device);
    const h = 1;
    const positions = new Float32Array([
        -h, 0, 0, h, 0, 0, 0, -h, 0, 0, h, 0, 0, 0, -h, 0, 0, h
    ]);
    mesh.setPositions(positions);
    mesh.update(PRIMITIVE_LINES);
    return mesh;
}

export type CoordWorldMarkerOptions = {
    /** In-progress GCP / pick: different color, dashed hit ring, “pending” label. */
    draft?: boolean;
    /** When set (e.g. map-plot GCP step), label shows “#N” (and DMS after coords are known). */
    labelIndex?: number;
};

export class CoordWorldMarker {
    readonly id: string;
    readonly hitEl: HTMLButtonElement;
    /** Screen-space DMS label — clickable for committed markers (draft: not interactive). */
    readonly labelEl: HTMLDivElement;
    private readonly scene: Scene;
    private readonly mesh: Mesh;
    private readonly material: StandardMaterial;
    private readonly entity: Entity;
    readonly world: Vec3 = new Vec3();
    private readonly draft: boolean;
    private readonly labelIndex?: number;

    constructor(scene: Scene, markersHost: HTMLElement, id: string, worldPos: Vec3, lat: number, lng: number, opts?: CoordWorldMarkerOptions) {
        this.id = id;
        this.scene = scene;
        this.draft = !!opts?.draft;
        this.labelIndex = typeof opts?.labelIndex === 'number' && Number.isFinite(opts.labelIndex) && opts.labelIndex > 0 ? Math.floor(opts.labelIndex) : undefined;
        const app = scene.app;

        this.mesh = createUnitCrossMesh(app.graphicsDevice);
        this.material = new StandardMaterial();
        this.material.cull = CULLFACE_NONE;
        this.material.diffuse.set(0, 0, 0);
        if (this.draft) {
            this.material.emissive.set(1, 0.82, 0.2);
            this.material.opacity = 0.92;
        } else {
            this.material.emissive.set(1, 1, 1);
            this.material.opacity = 1;
        }
        this.material.useLighting = false;
        this.material.depthWrite = false;
        this.material.depthTest = true;
        this.material.blendType = BLEND_NORMAL;
        this.material.update();

        const meshInstance = new MeshInstance(this.mesh, this.material);
        this.entity = new Entity(`volugraphCoordCross_${id}`, app);
        this.entity.addComponent('render', {
            meshInstances: [meshInstance],
            layers: [scene.gizmoLayer.id]
        });
        scene.contentRoot.addChild(this.entity);

        this.world.copy(worldPos);
        this.entity.setPosition(worldPos.x, worldPos.y, worldPos.z);
        this.syncScale();

        this.hitEl = document.createElement('button');
        this.hitEl.type = 'button';
        this.hitEl.className = this.draft ? 'coord-marker-hit coord-marker-hit--draft' : 'coord-marker-hit';
        this.hitEl.setAttribute('data-coord-id', id);
        this.hitEl.style.cssText = this.draft ?
            'position:absolute;width:28px;height:28px;margin:-14px 0 0 -14px;' +
              'border-radius:50%;border:2px dashed rgba(255,220,120,0.9);' +
              'background:rgba(255,200,40,0.15);cursor:pointer;padding:0;' :
            'position:absolute;width:28px;height:28px;margin:-14px 0 0 -14px;' +
              'border-radius:50%;border:1px solid rgba(255,255,255,0.35);' +
              'background:rgba(255,255,255,0.08);cursor:pointer;padding:0;';
        markersHost.appendChild(this.hitEl);

        this.labelEl = document.createElement('div');
        this.labelEl.className = this.draft ? 'coord-marker-label' : 'coord-marker-label coord-marker-label--interactive';
        if (!this.draft) {
            this.labelEl.setAttribute('role', 'button');
            this.labelEl.setAttribute('tabindex', '0');
            this.labelEl.setAttribute('aria-label', 'GCP coordinates — click to edit or delete');
        }
        this.labelEl.style.cssText =
            'position:absolute;white-space:pre;line-height:1.25;' +
            'font-family:ui-monospace,Consolas,monospace;font-size:11px;color:#fff;' +
            'text-shadow:0 0 4px #000,0 1px 2px #000;padding:2px 6px;' +
            'background:rgba(0,0,0,0.45);border-radius:4px;box-sizing:border-box;' +
            (this.draft ? 'pointer-events:none;' : 'border:1px solid rgba(255,255,255,0);');
        markersHost.appendChild(this.labelEl);

        if (this.draft) {
            this.labelEl.textContent =
                this.labelIndex !== undefined ? `#${this.labelIndex}` : 'Pick coordinates…';
        } else {
            this.setLatLng(lat, lng);
        }
    }

    private syncScale(): void {
        const sr = Math.max(this.scene.camera.sceneRadius, 1e-4);
        const s = sr * (this.draft ? 0.018 : 0.015);
        this.entity.setLocalScale(s, s, s);
    }

    setLatLng(lat: number, lng: number): void {
        if (this.draft && (!Number.isFinite(lat) || !Number.isFinite(lng))) {
            this.labelEl.textContent =
                this.labelIndex !== undefined ? `#${this.labelIndex}` : 'Pick coordinates…';
            return;
        }
        const dms = `${formatDegDms(lat, 'lat')}\n${formatDegDms(lng, 'lng')}`;
        this.labelEl.textContent = this.labelIndex !== undefined ? `#${this.labelIndex}\n${dms}` : dms;
    }

    setWorldPosition(x: number, y: number, z: number): void {
        this.world.set(x, y, z);
        this.entity.setPosition(x, y, z);
    }

    updateScreenLayout(): void {
        this.syncScale();
        const w = this.scene.canvas.clientWidth;
        const h = this.scene.canvas.clientHeight;
        this.scene.camera.worldToScreen(this.world, screenVec);
        if (
            screenVec.z <= 0 ||
            !Number.isFinite(screenVec.x) ||
            !Number.isFinite(screenVec.y)
        ) {
            this.hitEl.classList.add('coord-marker-hidden');
            this.labelEl.classList.add('coord-marker-hidden');
            return;
        }
        const sx = screenVec.x;
        const sy = screenVec.y;
        if (sx < -0.05 || sx > 1.05 || sy < -0.05 || sy > 1.05) {
            this.hitEl.classList.add('coord-marker-hidden');
            this.labelEl.classList.add('coord-marker-hidden');
            return;
        }
        this.hitEl.classList.remove('coord-marker-hidden');
        this.labelEl.classList.remove('coord-marker-hidden');
        const px = sx * w;
        const py = sy * h;
        this.hitEl.style.left = `${px}px`;
        this.hitEl.style.top = `${py}px`;
        this.labelEl.style.left = `${px + 18}px`;
        this.labelEl.style.top = `${py - 8}px`;
    }

    destroy(): void {
        try {
            this.entity.destroy();
        } catch {
            /* ignore */
        }
        try {
            this.mesh.destroy();
        } catch {
            /* ignore */
        }
        try {
            this.material.destroy();
        } catch {
            /* ignore */
        }
        this.hitEl.remove();
        this.labelEl.remove();
    }
}

export function formatCoordDmsLines(lat: number, lng: number): string {
    return `${formatDegDms(lat, 'lat')}\n${formatDegDms(lng, 'lng')}`;
}

/** Decimal places for map readouts & pre-filled decimal inputs (~1.1 m latitude at equator). */
export const COORD_UI_DECIMAL_PLACES = 5;

export function formatDecimalDegWithHemisphere(value: number, axis: 'lat' | 'lng', dp = COORD_UI_DECIMAL_PLACES): string {
    if (!Number.isFinite(value)) return '—';
    const abs = Math.abs(value);
    const hemi = axis === 'lat' ? (value >= 0 ? 'N' : 'S') : value >= 0 ? 'E' : 'W';
    return `${abs.toFixed(dp)}° ${hemi}`;
}

export function formatDecimalLatLngPairLine(lat: number, lng: number, dp = COORD_UI_DECIMAL_PLACES): string {
    return `${formatDecimalDegWithHemisphere(lat, 'lat', dp)}, ${formatDecimalDegWithHemisphere(lng, 'lng', dp)}`;
}

/** Rounded string for `<input type="number">` when pre-filling from map picks. */
export function formatCoordDecimalForNumericInput(value: number, dp = COORD_UI_DECIMAL_PLACES): string {
    if (!Number.isFinite(value)) return '';
    return value.toFixed(dp);
}
